/* eslint-disable fp/no-mutating-methods */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'

import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import useSession from 'src/authentication/hooks/use-session'
import {
  QuizFinalResults,
  QuizQuestionSelection,
  quizFinalResultsChanged,
  quizRestarted,
  selectClassID,
  selectClassesByCourseID,
  selectCourseByID,
  selectCourseID,
  selectCurrentQuizSelections,
  selectQuiz,
  selectQuizFinalResults,
  selectQuizSessionID,
} from '../classroom.slice'

let calculateResults = (submission: QuizQuestionSelection[]) =>
  axios
    .post<QuizFinalResults>(
      '/api/v2/classroom/quiz/result/calculate',
      submission,
    )
    .then(({ data }) => data)

let createClassEvent = (results: {
  classID: string
  status: 'started' | 'completed' | 'restarted'
}): Promise<void> => axios.post('/api/v2/classroom/stream-event/class', results)

export default () => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let session = useSession()
  let selections = useAppSelector(selectCurrentQuizSelections)
  let finalResults = useAppSelector(selectQuizFinalResults)
  let sessionID = useAppSelector(selectQuizSessionID)
  let quiz = useAppSelector(selectQuiz)
  let courseID = useAppSelector(selectCourseID)
  let classID = useAppSelector(selectClassID)
  let classes = useAppSelector(selectClassesByCourseID(courseID))
  let course = useAppSelector(selectCourseByID(courseID))
  let [classHasCert, setClassHasCert] = useState<boolean | null>(null)

  let isFoamfratCourse = !course?.linkedServiceID
  let showSurveyModal =
    finalResults?.isPassing && classHasCert && isFoamfratCourse

  useEffect(() => {
    if (finalResults && session?.userId) {
      axios
        .post('/api/v3/classroom/quiz/event', {
          quizEvent: {
            sessionID,
            userID: session?.userId ?? '',
            quizID: quiz.id,
            status: 'completed',
          },
        })
        .catch(() => Promise.resolve())
        .then(() => {
          if (finalResults?.isPassing) {
            return createClassEvent({
              classID: quiz.classID,
              status: 'completed',
            })
          }
          return Promise.resolve()
        })
        .then(() => {
          if (classes?.length) {
            setClassHasCert(
              classes?.length === 1 ||
                classes
                  .sort((a, b) => a.sequence - b.sequence)
                  .findIndex((ic) => ic.id === quiz.classID) +
                  1 ===
                  classes.length,
            )
          }
        })
    }
  }, [finalResults, session])

  useEffect(() => {
    calculateResults(selections).then((data) => {
      dispatch(quizFinalResultsChanged(data))
    })
  }, [])

  return (
    <div className="text-center p-4">
      <Dialog open={!!showSurveyModal} onClose={undefined}>
        <DialogTitle>Complete Your CE Survey</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You must complete the CE survey now to receive your certificate and
            be submitted to the national registry. Please do not navigate away
            before finishing the survey.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => navigate(`/classroom/survey/${courseID}/${classID}`)}
          >
            Take me to the survey
          </Button>
        </DialogActions>
      </Dialog>
      {finalResults && (
        <Typography variant="h4">Score: {finalResults?.score}</Typography>
      )}
      {finalResults && classHasCert != null && (
        <h2>
          {}
          {finalResults?.isPassing ? (
            classHasCert ? (
              <>
                <Typography>Congratulations, you passed!</Typography>
                <Button
                  className="mt-4"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    navigate(`/classroom/survey/${courseID}/${classID}`)
                  }}
                >
                  Continue to CE
                </Button>
              </>
            ) : (
              <>
                <Typography>
                  Congratulations, you passed! This is one of multiple classes
                  in this course. You may now take the next class.
                </Typography>
                <Button
                  className="mt-4"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    navigate(`/self-paced/course/${courseID}`)
                  }}
                >
                  Return to Classes
                </Button>
              </>
            )
          ) : (
            <>
              <Typography>
                Oops, you did not score high enough to obtain a CE, please try
                again
              </Typography>
              <Button
                className="mt-4"
                color="primary"
                variant="contained"
                onClick={() => dispatch(quizRestarted())}
              >
                Try Again
              </Button>
            </>
          )}
        </h2>
      )}
    </div>
  )
}
